import apiClient from "../apiClient";
import { COMMON_ENDPOINTS } from "../endpoints/common";
import { normalizeCreateBookingResponse } from "@/utils/bookingHelpers";
import { normalizePdfDownloadResponse } from "@/utils/downloadFile";
import {
  getReviewResponseMessage,
  isReviewSuccess,
} from "@/utils/reviewForm";

export const fetchOffers = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.offers, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching offers:", error);
    throw error;
  }
};

export const fetchDeals = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.deals, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching deals:", error);
    throw error;
  }
};

export const fetchDealsModuleData = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.dealsModuleData, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching deals module data:", error);
    throw error;
  }
};

export const fetchReviews = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.reviews, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching reviews:", error);
    throw error;
  }
};

export const fetchReviewsList = async ({ moduleType, slug } = {}) => {
  try {
    const params = { moduleType };
    if (slug) params.slug = slug;

    const response = await apiClient.get(COMMON_ENDPOINTS.reviewsList, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching reviews list:", error);
    throw error;
  }
};

export const saveReview = async (formData) => {
  try {
    const response = await apiClient.post(COMMON_ENDPOINTS.saveReview, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const data = response.data;

    if (!isReviewSuccess(data)) {
      const error = new Error(
        getReviewResponseMessage(data, "Failed to submit review.")
      );
      error.response = { data };
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error saving review:", error);
    throw error;
  }
};

export const fetchBanners = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.banners, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching banners:", error);
    throw error;
  }
};

export const subscribeNewsletter = async (payload = {}) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.newsletter,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error subscribing newsletter:", error);
    throw error;
  }
};

export const verifyPanNumber = async (payload) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.verifyPan,
      payload
    );

    if (response.data.status === "Success") {
      return response.data;
    }
    return null;
  } catch (error) {
    throw error;
  }
};

export const verifyGST = async (payload) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.verifyGst, {
      params: payload,
    });

    if (String(response.data.status || "").toLowerCase() === "success") {
      return response.data;
    }
    return null;
  } catch (error) {
    throw error;
  }
};

export const createBooking = async (payload) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.CreateBooking,
      payload
    );
    const normalized = normalizeCreateBookingResponse(response.data);

    if (!normalized) {
      throw new Error(
        response.data?.message ||
        response.data?.Message ||
        "Unable to create booking."
      );
    }

    return normalized;
  } catch (error) {
    console.error(
      "Error creating booking:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

export const getCountryList = async () => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.CountryList);
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching country list:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

export const getIndiaStateList = async () => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.IndiaStateList);
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching India state list:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

export const getStatesByCountryId = async (countryId) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.statesByCountryId, {
      params: { countryId },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching states by country:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

export const getUserPersonalDetails = async (userId) => {
  try {
    const response = await apiClient.get(
      COMMON_ENDPOINTS.GetUserPersonalDetails,
      {
        params: { userId },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUserPersonalDetails = async (params) => {
  try {
    const response = await apiClient.get(
      COMMON_ENDPOINTS.PostOrUpdateUserPersonalDetails,
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const bookingPageCoupons = async (payload) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.bookingPageCoupons, {
      params: payload,
    });
    return response.data;
  } catch (error) {
    console.error(
      "Error fetching booking page coupons:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

export const fetchCouponList = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.couponList, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching coupon list:", error);
    throw error;
  }
};

export const fetchCouponCategoryList = async () => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.couponCategoryList, { params: { RequestFrom: 1 } });
    return response.data;
  } catch (error) {
    console.error("Error fetching coupon categories:", error);
    throw error;
  }
};

export const fetchCouponModuleList = async () => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.couponModuleList, { params: { RequestFrom: 1 } });
    return response.data;
  } catch (error) {
    console.error("Error fetching coupon modules:", error);
    throw error;
  }
};

export const fetchCouponDetail = async ({ couponSlug } = {}) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.couponDetail, {
      params: { CouponSlug: couponSlug },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching coupon detail:", error);
    throw error;
  }
};

export const fetchCouponHomePageHeading = async () => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.couponHomePageHeading);
    return response.data;
  } catch (error) {
    console.error("Error fetching coupon home page heading:", error);
    throw error;
  }
};

export const fetchSeasonalDeals = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.seasonalDeals, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching seasonal deals:", error);
    throw error;
  }
};

export const fetchSeasonalDealsData = async ({ slug } = {}) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.seasonalDealsData, {
      params: { slug },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching seasonal deals data:", error);
    throw error;
  }
};

export const VerifyCoupon = async (payload) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.VerifyCoupon,
      null,
      { params: payload }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error verifying coupon:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

export const submitEnquiryForm = async (params) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.submitEnquiryForm,
      null,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error("Error submitting enquiry form:", error);
    throw error;
  }
};

export const fetchMissionStatement = async (pageType) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.missionStatement, {
      params: { PageType: pageType },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching mission statement:", error);
    throw error;
  }
};

export const submitContactUs = async (formData) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.submitContactUs,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error submitting contact form:", error);
    throw error;
  }
};

export const fetchFooterHeading = async () => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.footerHeading);
    return response.data;
  } catch (error) {
    console.error("Error fetching footer heading:", error);
    throw error;
  }
};

export const fetchCart = async (params = {}) => {
  try {
    const response = await apiClient.post(COMMON_ENDPOINTS.getCart, null, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching cart:", error?.response?.data || error.message);
    throw error;
  }
};

export const removeCart = async (params = {}) => {
  try {
    const response = await apiClient.post(COMMON_ENDPOINTS.removeCart, null, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error removing cart item:", error?.response?.data || error.message);
    throw error;
  }
};

export const fetchThingsToDo = async (slug) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.getThingsToDo, {
      params: { slug },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching things to do page:", error);
    throw error;
  }
};

export const fetchCountryCityPageData = async (slug = "") => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.getCountryCityPageData, {
      params: { slug },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching country/city page data:", error);
    throw error;
  }
};

export const fetchBookingsList = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.bookingsList, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching bookings list:", error);
    throw error;
  }
};

export const getCityListByCountryId = async (countryId) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.cityListByCountryId, {
      params: { countryId },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching city list:", error);
    throw error;
  }
};

export const verifyIciciPayment = async (params) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.verifyIciciPayment, params
    );
    return response.data;
  } catch (error) {
    console.error("Error verifying ICICI payment:", error);
    throw error;
  }
};

export const verifyRazorpayPayment = async (params) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.verifyRazorpayPayment, params
    );
    return response.data;
  } catch (error) {
    console.error("Error verifying Razorpay payment:", error);
    throw error;
  }
};

export const verifyStripePayment = async (params) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.verifyStripePayment, params
    );
    return response.data;
  } catch (error) {
    console.error("Error verifying Stripe payment:", error);
    throw error;
  }
};

export const downloadInvoice = async (params) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.downloadInvoice, {
      params: {
        BookingRefId:
          params?.bookingRefId ??
          params?.BookingRefId ??
          params?.bookingReference ??
          params?.BookingReference,
      },
      responseType: "arraybuffer",
    });
    return normalizePdfDownloadResponse(response);
  } catch (error) {
    console.error("Error downloading invoice:", error);
    throw error;
  }
};

export const fetchBookingDetails = async (refIdKey) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.getBookingDetails, {
      params: { RefIdKey: refIdKey },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching booking details:", error);
    throw error;
  }
};

export const downloadInvoicePdfLink = async (bookingRefId) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.downloadInvoice, {
      params: { BookingRefId: bookingRefId },
      responseType: "arraybuffer",
    });
    return normalizePdfDownloadResponse(response);
  } catch (error) {
    console.error("Error fetching invoice PDF link:", error);
    throw error;
  }
};

export const downloadVoucherPdfLink = async (bookingRefId) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.downloadVoucher, {
      params: { BookingRefId: bookingRefId },
      responseType: "arraybuffer",
    });
    return normalizePdfDownloadResponse(response);
  } catch (error) {
    console.error("Error fetching voucher PDF link:", error);
    throw error;
  }
};

export const saveHomePageEnquiryPopup = async (payload) => {
  try {
    const response = await apiClient.post(
      COMMON_ENDPOINTS.saveHomePageEnquiryPopup,
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error saving home page enquiry popup:", error);
    throw error;
  }
};

export const fetchEnquiryDestinations = async (prefix = "") => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.getEnquiryDestination, {
      params: { prefix },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching enquiry destinations:", error);
    throw error;
  }
};

export const fetchEngageList = async (moduleType) => {
  try {
    const response = await apiClient.get(COMMON_ENDPOINTS.getEngageList, {
      params: { moduleType },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching engage list:", error);
    throw error;
  }
};

export const fetchRandomReviewList = async ({
  moduleType = "Holiday",
  noOfreview = 5,
  rating,
} = {}) => {
  try {
    const params = { moduleType, noOfreview };
    if (rating != null) params.rating = rating;

    const response = await apiClient.get(COMMON_ENDPOINTS.randomReviewList, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching random review list:", error);
    throw error;
  }
};
